import { InputAdornment } from "@mui/material"
import type { FC } from "react"
import { useRef } from "react"
import { useFormContext } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import { GoodsReceiptProductLayout } from "../../../../components"
import type { IncomingPurchaseOrdersFormValues } from "../../schemas"

import { FormNumericField } from "@/components/UiKit"
import type { OrderLine } from "@/graphql/codegen/graphql"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { runWithGuardRef } from "@/utils/forms"
import { roundToPrecision } from "@/utils/number"

interface IncomingPurchaseOrderLineFormProps {
  purchaseOrderLine: OrderLine
  purchaseOrderIndex: number
  purchaseOrderLineIndex: number
  canMaintain: boolean
  isResettingRef?: React.MutableRefObject<boolean>
}

export const IncomingPurchaseOrderLineForm: FC<IncomingPurchaseOrderLineFormProps> = ({
  purchaseOrderLine,
  purchaseOrderIndex,
  purchaseOrderLineIndex,
  canMaintain,
  isResettingRef,
}) => {
  const { control, watch, setValue } = useFormContext<IncomingPurchaseOrdersFormValues>()
  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const isUpdatingRef = useRef(false)

  const baseFieldPath = `purchaseOrders.${purchaseOrderIndex}.lines.${purchaseOrderLineIndex}` as const
  const fieldPaths = {
    uomId: `${baseFieldPath}.uomId` as const,
    palletsCount: `${baseFieldPath}.palletsCount` as const,
    unitsCount: `${baseFieldPath}.unitsCount` as const,
    quantity: `${baseFieldPath}.quantity` as const,
  }
  const product = purchaseOrderLine.product!
  const packageWeight = product.packageUsWeight!
  const palletWeight = product.palletUsWeight!
  const uomId = watch(fieldPaths.uomId)
  const uom = uoms.find((uom) => uom.id === uomId)

  const handlePalletsCountChange = (value: number | null) => {
    if (isResettingRef?.current || isUpdatingRef.current) {
      return
    }

    runWithGuardRef(() => {
      const quantity = value === null ? null : roundToPrecision(value * palletWeight, 2)
      const unitsCount = quantity === null ? null : roundToPrecision(quantity / packageWeight, 2)

      setValue(fieldPaths.unitsCount, unitsCount, { shouldValidate: true })
      setValue(fieldPaths.quantity, quantity, { shouldValidate: true })
    }, isUpdatingRef)
  }

  const handleUnitsCountChange = (value: number | null) => {
    if (isResettingRef?.current || isUpdatingRef.current) {
      return
    }

    runWithGuardRef(() => {
      const quantity = value === null ? null : roundToPrecision(value * packageWeight, 2)
      const palletsCount = quantity === null ? null : roundToPrecision(quantity / palletWeight, 2)

      setValue(fieldPaths.quantity, quantity, { shouldValidate: true })
      setValue(fieldPaths.palletsCount, palletsCount, { shouldValidate: true })
    }, isUpdatingRef)
  }

  const handleQuantityChange = (value: number | null) => {
    if (isResettingRef?.current || isUpdatingRef.current) {
      return
    }

    runWithGuardRef(() => {
      const unitsCount = value === null ? null : roundToPrecision(value / packageWeight, 2)
      const palletsCount = value === null ? null : roundToPrecision(value / palletWeight, 2)

      setValue(fieldPaths.unitsCount, unitsCount, { shouldValidate: true })
      setValue(fieldPaths.palletsCount, palletsCount, { shouldValidate: true })
    }, isUpdatingRef)
  }

  return (
    <GoodsReceiptProductLayout product={product}>
      <FormNumericField
        control={control}
        name={fieldPaths.palletsCount}
        label='Est. Pallets'
        className='w-[11%]'
        allowNegative={false}
        decimalScale={2}
        disabled={!canMaintain}
        onChange={handlePalletsCountChange}
        data-testid='pallets-count-field'
      />
      <FormNumericField
        control={control}
        name={fieldPaths.unitsCount}
        label='Est. Units'
        className='w-[11%]'
        allowNegative={false}
        decimalScale={2}
        disabled={!canMaintain}
        onChange={handleUnitsCountChange}
        data-testid='units-count-field'
      />
      <FormNumericField
        control={control}
        name={fieldPaths.quantity}
        label='Est. Total UoM'
        className='w-[11%]'
        allowNegative={false}
        decimalScale={2}
        disabled={!canMaintain}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end' className='text-sm'>
                {uom?.name}
              </InputAdornment>
            ),
          },
        }}
        onChange={handleQuantityChange}
        data-testid='quantity-field'
      />
    </GoodsReceiptProductLayout>
  )
}
